import React, { useEffect, useState } from 'react';
import { apiGet } from '../../lib/api';
import { RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const TARGETS = {
  maintenance_avg_resolution_days:  { label: 'Avg Resolution Days',        target: 3,   unit: 'days',  lowerBetter: true },
  maintenance_sla_breach_pct:       { label: 'SLA Breach %',               target: 10,  unit: '%',     lowerBetter: true },
  supply_stockout_count_mtd:        { label: 'Stockouts (MTD)',             target: 0,   unit: '',      lowerBetter: true },
  supply_low_stock_count:           { label: 'Low Stock Items',             target: 5,   unit: '',      lowerBetter: true },
  amc_renewed_on_time_pct:          { label: 'AMC Renewed On Time',         target: 95,  unit: '%',     lowerBetter: false },
  travel_requests_processed_mtd:    { label: 'Travel Requests (MTD)',       target: null, unit: '',     lowerBetter: false },
  admin_vendor_approved_count:      { label: 'Approved Vendors',            target: null, unit: '',     lowerBetter: false },
  admin_vendor_pending_count:       { label: 'Pending Vendor Approvals',    target: 0,   unit: '',      lowerBetter: true },
  visitor_count_mtd:                { label: 'Visitors (MTD)',              target: null, unit: '',     lowerBetter: false },
  admin_pr_within_budget_pct:       { label: 'PR Approval Rate',            target: 80,  unit: '%',     lowerBetter: false },
};

function trend(value, target, lowerBetter) {
  if (target === null || target === undefined) return 'neutral';
  if (lowerBetter) return value <= target ? 'good' : 'bad';
  return value >= target ? 'good' : 'bad';
}

const TREND_STYLE = {
  good:    { icon: TrendingUp,   cls: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-200' },
  bad:     { icon: TrendingDown, cls: 'text-red-600',     bg: 'bg-red-50 border-red-200' },
  neutral: { icon: Minus,        cls: 'text-slate-400',   bg: 'bg-white border-slate-200' },
};

function KPICard({ metaKey, value }) {
  const meta = TARGETS[metaKey] || { label: metaKey, target: null, unit: '', lowerBetter: false };
  const t = trend(value, meta.target, meta.lowerBetter);
  const { icon: Icon, cls, bg } = TREND_STYLE[t];
  const display = typeof value === 'number' ? (Number.isInteger(value) ? value : value.toFixed(1)) : (value ?? '—');

  return (
    <div className={`border rounded-sm p-4 ${bg}`} data-testid={`kpi-card-${metaKey}`}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500 leading-tight max-w-[140px]">
          {meta.label}
        </div>
        <Icon size={14} className={cls} />
      </div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="text-2xl font-display font-extrabold text-[#3b4872]">{display}</span>
        {meta.unit && <span className="text-xs text-slate-400">{meta.unit}</span>}
      </div>
      {meta.target !== null && (
        <div className="mt-1 text-[10px] text-slate-400">
          Target: {meta.target}{meta.unit}
        </div>
      )}
    </div>
  );
}

export default function AdminKPI() {
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setKpi(await apiGet('/admin/kpi')); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const TOP_KEYS = [
    'maintenance_avg_resolution_days', 'maintenance_sla_breach_pct',
    'supply_stockout_count_mtd', 'supply_low_stock_count',
    'amc_renewed_on_time_pct', 'admin_pr_within_budget_pct',
    'visitor_count_mtd', 'travel_requests_processed_mtd',
    'admin_vendor_approved_count', 'admin_vendor_pending_count',
  ];

  return (
    <div data-testid="admin-kpi" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">Admin · SOP J</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">KPI Dashboard</h1>
          {kpi?.period && <p className="text-sm text-slate-500 mt-1">Period: {kpi.period}</p>}
        </div>
        <button onClick={load} className="flex items-center gap-1.5 text-xs px-3 py-2 border border-slate-200 rounded-sm hover:bg-slate-50">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !kpi ? (
        <div className="p-12 text-center text-sm text-slate-400">Loading KPIs…</div>
      ) : kpi ? (
        <>
          {/* Main KPI grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {TOP_KEYS.map(k => (
              <KPICard key={k} metaKey={k} value={kpi[k]} />
            ))}
          </div>

          {/* Admin Assets sub-section */}
          {kpi.admin_assets && (
            <div>
              <h2 className="font-display font-bold text-sm uppercase tracking-wider text-slate-500 mb-3">Admin Assets (SOP I)</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Assets</div>
                  <div className="text-2xl font-display font-extrabold text-[#3b4872] mt-2">{kpi.admin_assets.total}</div>
                </div>
                <div className={`border rounded-sm p-4 ${kpi.admin_assets.audit_due_count > 0 ? 'bg-amber-50 border-amber-200' : 'bg-white border-slate-200'}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Audit Due</div>
                  <div className={`text-2xl font-display font-extrabold mt-2 ${kpi.admin_assets.audit_due_count > 0 ? 'text-amber-700' : 'text-[#3b4872]'}`}>
                    {kpi.admin_assets.audit_due_count}
                  </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-sm p-4">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Disposed MTD</div>
                  <div className="text-2xl font-display font-extrabold text-[#3b4872] mt-2">{kpi.admin_assets.disposed_mtd}</div>
                </div>
                <div className={`border rounded-sm p-4 ${kpi.admin_assets.warranty_expiring_count > 0 ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Warranty Expiring (30d)</div>
                  <div className={`text-2xl font-display font-extrabold mt-2 ${kpi.admin_assets.warranty_expiring_count > 0 ? 'text-red-700' : 'text-[#3b4872]'}`}>
                    {kpi.admin_assets.warranty_expiring_count}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
